import { friesStatus, type CookStatus, type Fries } from "./food";

// The Daily Grind's coffee: what a ticket asks for, and how its parts are
// made. Pure data and functions; `shift.ts` moves them between stations.
//
// An espresso shot and a pitcher of milk both "cook" like fries: from 0
// towards done at 1, past it to ruined at 2 (`friesStatus`'s bands). A
// shot pulled short is sour, one left running is bitter; milk taken off the
// wand too soon is cold, left on it scalds. The grinder's dose is a fill,
// like a soda cup: stop in the band, or it's thin, or it overflows.

export type Syrup = "vanilla" | "caramel" | "hazelnut";

/** The syrup pumps along the back counter, left to right. */
export const SYRUPS: readonly Syrup[] = ["vanilla", "caramel", "hazelnut"];

/** What a coffee ticket asks for in one paper cup. */
export interface CoffeeBuild {
  shots: number;
  milk: boolean;
  syrup?: Syrup;
}

/** A shot pulling (or pulled), or milk steaming (or steamed): heat over time, done at 1. */
export type Heated = Fries;

/** What's in a paper cup on the bar or in your hands. */
export interface CoffeeCup {
  shots: Heated[];
  milk: Heated | null;
  syrups: Syrup[];
}

export const EMPTY_CUP: CoffeeCup = { shots: [], milk: null, syrups: [] };

/** At most this many shots in a cup. */
export const MAX_SHOTS = 2;

/** The grinder's dose in a portafilter: right from `DOSE_AT` to the brim at 1, over it spills grounds. */
export const DOSE_AT = 0.85;
export const MAX_DOSE = 1.15;

export type DoseStatus = "empty" | "thin" | "right" | "spilled";

export function doseStatus(dose: number): DoseStatus {
  if (dose <= 0) return "empty";
  if (dose > 1) return "spilled";
  return dose >= DOSE_AT ? "right" : "thin";
}

/** A shot or milk at a glance, for the markers and the HUD: the same bands as fries. */
export function heatStatus(h: Heated): { status: CookStatus; progress: number } {
  return friesStatus(h);
}

/** Done just right: past done, not yet past the warning that it's going over. */
function right(h: Heated): boolean {
  const s = heatStatus(h).status;
  return s === "done" || s === "burning";
}

/** The first way a cup falls short of a build, in words, or null when it's right. */
export function coffeeMismatch(c: CoffeeCup, b: CoffeeBuild): string | null {
  if (c.shots.length < b.shots) return b.shots - c.shots.length === 1 && c.shots.length ? "needs another shot" : b.shots > 1 ? "needs two shots" : "needs a shot";
  if (c.shots.length > b.shots) return "has a shot too many";
  for (const shot of c.shots) {
    if (!right(shot)) return heatStatus(shot).status === "burnt" ? "has a bitter shot (pulled too long)" : "has a sour shot (pulled too short)";
  }
  if (b.milk && !c.milk) return "needs steamed milk";
  if (!b.milk && c.milk) return "no milk in this one";
  if (c.milk && !right(c.milk)) return heatStatus(c.milk).status === "burnt" ? "has scalded milk" : "has cold milk";
  if (b.syrup && !c.syrups.includes(b.syrup)) return `needs ${b.syrup}`;
  for (const s of c.syrups) if (s !== b.syrup) return `no ${s} in this one`;
  return null;
}

/** The build, in words: "2 shots, steamed milk, vanilla". */
export function describeCoffee(b: CoffeeBuild): string {
  const parts = [b.shots === 1 ? "1 shot" : `${b.shots} shots`];
  if (b.milk) parts.push("steamed milk");
  if (b.syrup) parts.push(b.syrup);
  return parts.join(", ");
}

/** How many ways a cup differs from a build, for picking the closest ticket. */
export function coffeeDifferences(c: CoffeeCup, b: CoffeeBuild): number {
  let n = Math.abs(c.shots.length - b.shots) + (b.milk === !!c.milk ? 0 : 1);
  if (b.syrup && !c.syrups.includes(b.syrup)) n++;
  n += c.syrups.filter((s) => s !== b.syrup).length;
  return n;
}

/** The names baristas call out. A ticket's comes from its order number, never the day's dice. */
const NAMES = [
  "Maya", "Leo", "Priya", "Sam", "Jordan", "Ava", "Mateo", "Zoe", "Kai", "Nora",
  "Eli", "Rosa", "Theo", "Ivy", "Omar", "June", "Felix", "Hana", "Noah", "Lucia",
  "Ben", "Aisha", "Remy", "Tess",
];

export function nameFor(no: number): string {
  return NAMES[(no * 7 + 3) % NAMES.length]!;
}
